import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  MapPin,
  Phone,
  Mail,
  Building,
  User,
  Search,
  FileText,
  Euro
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const clientIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function MapController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 12);
    }
  }, [center, map]);
  return null;
}

export default function ClientsMapPage() {
  const { api, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [mapCenter, setMapCenter] = useState([39.5, -8.0]); // Portugal center
  
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await api().get('/contacts/map');
      setClients(response.data);
      
      // Center map on first client
      if (response.data.length > 0 && response.data[0].lat) {
        setMapCenter([response.data[0].lat, response.data[0].lng]);
      }
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadClientOrders = async (clientId) => {
    try {
      const response = await api().get(`/orders/client/${clientId}`);
      setClientOrders(response.data);
    } catch (error) {
      console.error('Error loading orders', error);
    }
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setMapCenter([client.lat, client.lng]);
    loadClientOrders(client.id);
  };

  const downloadClientPDF = async (clientId, clientName) => {
    try {
      const response = await api().get(`/orders/client/${clientId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Historico_${clientName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descarregado!');
    } catch (error) {
      toast.error('Erro ao descarregar PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-map-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? 'Todos os clientes' : 'Os seus clientes'} • {filteredClients.length} com localização
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
        {/* Client List */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-[Manrope]">Clientes</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              <div className="space-y-1 p-4 pt-0">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedClient?.id === client.id 
                        ? 'bg-amber-50 border-amber-300' 
                        : 'hover:bg-slate-50'
                      }
                    `}
                    onClick={() => handleClientClick(client)}
                    data-testid={`client-map-item-${client.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-full">
                        <User className="h-4 w-4 text-amber-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{client.name}</p>
                        {client.company && (
                          <p className="text-xs text-muted-foreground truncate">{client.company}</p>
                        )}
                        {client.address && (
                          <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3" />
                            {client.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {filteredClients.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Nenhum cliente com localização</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Map and Client Details */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Map */}
          <Card className="flex-1 overflow-hidden">
            <CardContent className="p-0 h-full">
              <MapContainer 
                center={mapCenter} 
                zoom={8} 
                style={{ height: '100%', width: '100%', minHeight: '300px' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapController center={mapCenter} />
                
                {filteredClients.map((client) => (
                  <Marker 
                    key={client.id}
                    position={[client.lat, client.lng]}
                    icon={clientIcon}
                    eventHandlers={{
                      click: () => handleClientClick(client)
                    }}
                  >
                    <Popup>
                      <div className="text-sm min-w-[200px]">
                        <p className="font-bold">{client.name}</p>
                        {client.company && <p className="text-muted-foreground">{client.company}</p>}
                        {client.phone && (
                          <p className="flex items-center gap-1 mt-1">
                            <Phone className="h-3 w-3" /> {client.phone}
                          </p>
                        )}
                        {client.email && (
                          <p className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {client.email}
                          </p>
                        )}
                        {client.address && (
                          <p className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {client.address}
                          </p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </CardContent>
          </Card>

          {/* Selected Client Details */}
          {selectedClient && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-[Manrope]">{selectedClient.name}</CardTitle>
                    {selectedClient.company && (
                      <p className="text-sm text-muted-foreground">{selectedClient.company}</p>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => downloadClientPDF(selectedClient.id, selectedClient.name)}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    PDF Histórico
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {selectedClient.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{selectedClient.phone}</span>
                    </div>
                  )}
                  {selectedClient.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate">{selectedClient.email}</span>
                    </div>
                  )}
                  {selectedClient.position && (
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{selectedClient.position}</span>
                    </div>
                  )}
                  {selectedClient.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate">{selectedClient.address}</span>
                    </div>
                  )}
                </div>
                
                {/* Recent Orders */}
                {clientOrders.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Últimas Encomendas</h4>
                    <div className="space-y-2">
                      {clientOrders.slice(0, 3).map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div>
                            <span className="font-mono text-sm">{order.order_number}</span>
                            <Badge className="ml-2" variant="outline">{order.status}</Badge>
                          </div>
                          <span className="font-medium tabular-nums">{formatCurrency(order.total)}</span>
                        </div>
                      ))}
                    </div>
                    {clientOrders.length > 3 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        +{clientOrders.length - 3} mais encomendas
                      </p>
                    )}
                  </div>
                )}
                
                {clientOrders.length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhuma encomenda registada</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
